import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import toast from 'react-hot-toast';
import { organizationService } from '../services';
import { Organization } from '../types';
import { useAuth } from './AuthContext';

interface OrganizationState {
  currentOrganization: Organization | null;
  isLoading: boolean;
  error: string | null;
}

type OrganizationAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CURRENT_ORGANIZATION'; payload: Organization | null }
  | { type: 'SET_ERROR'; payload: string | null };

interface OrganizationContextType extends OrganizationState {
  loadCurrentOrganization: () => Promise<void>;
  clearError: () => void;
  clearOrganization: () => void;
}

const initialState: OrganizationState = {
  currentOrganization: null,
  isLoading: false,
  error: null,
};

function organizationReducer(
  state: OrganizationState,
  action: OrganizationAction
): OrganizationState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_CURRENT_ORGANIZATION':
      return {
        ...state,
        currentOrganization: action.payload,
        isLoading: false,
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };
    default:
      return state;
  }
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined
);

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(organizationReducer, initialState);
  const { user, isAuthenticated } = useAuth();

  const loadCurrentOrganization = useCallback(async (): Promise<void> => {
    if (!user?.organization_id) {
      dispatch({
        type: 'SET_ERROR',
        payload: 'Usuário não possui organização associada',
      });
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Busca a organização do usuário logado
      const organization = await organizationService.getById(
        user.organization_id
      );

      if (organization) {
        dispatch({ type: 'SET_CURRENT_ORGANIZATION', payload: organization });
        localStorage.setItem(
          'current_organization_id',
          user.organization_id.toString()
        );
      } else {
        throw new Error(
          `Organização ID ${user.organization_id} não encontrada`
        );
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erro ao carregar organização';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      toast.error(errorMessage);
    }
  }, [user]);

  const clearError = useCallback((): void => {
    dispatch({ type: 'SET_ERROR', payload: null });
  }, []);

  const clearOrganization = useCallback((): void => {
    dispatch({ type: 'SET_CURRENT_ORGANIZATION', payload: null });
    localStorage.removeItem('current_organization_id');
  }, []);

  // Carrega a organização automaticamente quando o usuário está autenticado
  useEffect(() => {
    if (
      isAuthenticated &&
      user?.organization_id &&
      !state.currentOrganization &&
      !state.isLoading
    ) {
      loadCurrentOrganization();
    }
  }, [
    isAuthenticated,
    user?.organization_id,
    state.currentOrganization,
    state.isLoading,
    loadCurrentOrganization,
  ]);

  // Limpa a organização quando o usuário faz logout
  useEffect(() => {
    if (!isAuthenticated && state.currentOrganization) {
      clearOrganization();
    }
  }, [isAuthenticated, state.currentOrganization, clearOrganization]);

  const value: OrganizationContextType = {
    ...state,
    loadCurrentOrganization,
    clearError,
    clearOrganization,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error(
      'useOrganization must be used within an OrganizationProvider'
    );
  }
  return context;
}
